import { css, cx } from '@/styled-system/css'
import { splitCssProps } from '@/styled-system/jsx'
import { formInput, type FormInputVariantProps } from '@/styled-system/recipes'
import type { Assign, JsxStyleProps } from '@/styled-system/types'
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react'

export interface FormInputProps
  extends Assign<
      JsxStyleProps,
      Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>
    >,
    FormInputVariantProps {
  children?: ReactNode
  error?: boolean
  helperText?: ReactNode
  inputRef?: Ref<HTMLInputElement>
  label?: ReactNode
  leftElement?: ReactNode
  leftIcon?: ReactNode
  rightElement?: ReactNode
  rightIcon?: ReactNode
}

export const FormInput = forwardRef<HTMLDivElement, FormInputProps>(
  (props, ref) => {
    const [variantProps, rest] = formInput.splitVariantProps(props)
    const [cssProps, localProps] = splitCssProps(rest)
    const {
      children,
      className,
      disabled,
      error = false,
      helperText,
      id,
      inputRef,
      label,
      leftElement,
      leftIcon,
      rightElement,
      rightIcon,
      ...inputProps
    } = localProps
    const styles = formInput(variantProps)

    const isDisabled = disabled ? true : undefined
    const isInvalid = error ? true : undefined

    return (
      <div ref={ref} className={cx(styles.root, css(cssProps), className)}>
        {label && (
          <label
            className={styles.label}
            htmlFor={id}
            data-disabled={isDisabled}
            data-invalid={isInvalid}
          >
            {label}
          </label>
        )}

        <div className={styles.group}>
          {leftElement}

          <div
            className={cx(styles.fieldWrapper)}
            data-disabled={isDisabled}
            data-invalid={isInvalid}
          >
            {leftIcon && <div className={styles.leftIcon}>{leftIcon}</div>}

            <input
              ref={inputRef}
              id={id}
              className={styles.field}
              autoComplete='off'
              disabled={disabled}
              aria-invalid={error}
              {...inputProps}
            />

            {rightIcon && <div className={styles.rightIcon}>{rightIcon}</div>}
          </div>

          {rightElement}
        </div>

        {helperText && (
          <div
            className={styles.helperText}
            data-disabled={isDisabled}
            data-invalid={isInvalid}
          >
            {helperText}
          </div>
        )}
      </div>
    )
  }
)

FormInput.displayName = 'FormInput'
